import React from 'react'
import { LoginStepEmailForm } from './LoginStepEmailForm'
import { UserStateResponseEnum } from '../../../graphql/public/PublicSchemaTypes'
import { notification } from '../../../components/common/notifications/Notifications'
import { t } from '@lingui/macro'
import emailRegex from '../../../app/services/emailRegex'
import { navigateTo } from '../../../app/util/appHistory'
import { linkBuilder } from 'shared/linkBuilder'
import { useLoginContext } from '../login/LoginContext'
import { useIsAvailableForLoginMutation } from './Email.codegen'
import { observer } from 'mobx-react'
import { VerifyUserFormStep } from '../verification-user-form-step/VerifyUserFormStep'
import { AskPasswordStep } from '../ask-password/AskPasswordStep'
import { SetPasswordStep } from '../set-password/SetPasswordStep'

const useIsAvailableForLogin = () => {
  const { userIdentity, setUserHasEmail } = useLoginContext()
  const [fetch] = useIsAvailableForLoginMutation()

  return async () => {
    const { data } = await fetch({
      variables: {
        accountNameOrEmail: userIdentity
      }
    })
    if (!data?.isAvailableForLogin) {
      return null
    }
    const { email } = data.isAvailableForLogin
    setUserHasEmail(!!email)

    return data.isAvailableForLogin
  }
}

export const LoginEmailStep: React.FC = observer(() => {
  const ctx = useLoginContext()

  const isAvailableForLogin = useIsAvailableForLogin()
  const submit = async () => {
    const response = await isAvailableForLogin()
    if (!response) {
      return
    }

    switch (response.status) {
      case UserStateResponseEnum.NOT_ACTIVE:
        notification.error(t`This user was deactivated`)
        break
      case UserStateResponseEnum.NOT_FOUND:
        if (ctx.organisation?.allow_signup && !ctx.isAccountNameLoginOn) {
          ctx.setStepView(SetPasswordStep)
        } else {
          // even if user is not found, ask for password for security reasons
          ctx.setStepView(AskPasswordStep)
        }
        break
      case UserStateResponseEnum.NOT_VERIFIED_REQUEST_VERIFICATION:
        ctx.setIsExistingUser(true)
        ctx.setStepView(VerifyUserFormStep)
        break
      case UserStateResponseEnum.NOT_VERIFIED:
        ctx.setIsExistingUser(true)
        const isEmail = ctx.userIdentity && !!emailRegex.exec(ctx.userIdentity)
        if (!isEmail && ctx.isAccountNameLoginOn) {
          ctx.setStepView(AskPasswordStep)
        } else {
          navigateTo(linkBuilder.awaitingVerification(ctx.userIdentity))
        }
        break
      case UserStateResponseEnum.NOT_VERIFIED_NO_PASSWORD:
        ctx.setIsExistingUser(true)
        navigateTo(linkBuilder.resetPasswordSent(response.email || ''))
        break
      case UserStateResponseEnum.VERIFIED:
        ctx.setIsExistingUser(true)
        ctx.setStepView(AskPasswordStep)
        break
      case UserStateResponseEnum.FORBIDDEN:
        notification.error(t`This email address cannot be used`)
        break
    }
  }

  return <LoginStepEmailForm submit={submit} />
})

LoginEmailStep.displayName = 'LoginEmailStep'
